import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  useGetProfilesById,
  usePutProfiles,
  getGetProfilesByIdQueryKey,
  usePostProfileSync,
  useGetSchedules,
  getSourceTables,
  useGetAgentsById,
} from '@/api/control/control-plane/control-plane';
import {
  Copy,
  Check,
  Menu,
  Power,
  Pencil,
  Trash2,
  RefreshCw,
  Wand2,
  HelpCircle,
  EllipsisVertical,
  Zap,
} from 'lucide-react';
import { RefreshButton } from '@/components/RefreshButton';
import { EditProfileDialog } from '@/components/profiles/EditProfileDialog';
import { DeleteProfileDialog } from '@/components/profiles/DeleteProfileDialog';
import { SourceCard } from '@/components/profiles/sources/SourceCard';
import { TargetCard } from '@/components/profiles/targets/TargetCard';
import { AgentOfflineProfileAlert } from '@/components/agents/AgentOfflineProfileAlert';
import { DeleteScheduleDialog } from '@/components/profiles/schedules/DeleteScheduleDialog';
import { CreateScheduleDialog } from '@/components/profiles/schedules/CreateScheduleDialog';
import { QuietTimeCard } from '@/components/profiles/schedules/QuietTimeCard';
import { SyncConfirmationDialog } from '@/components/profiles/SyncConfirmationDialog';
import { BurstDialog } from '@/components/profiles/BurstDialog';
import { readBurstExpiresUtc } from '@/api/burst-control';
import { RunMetadataTable } from '@/components/runs/RunMetadataTable';
import type { ScheduleDto } from '@/api/control/missionImpactAPI.schemas';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useHelpMenu } from '@/contexts/HelpMenuContext';
import { HelpMenuBlade } from '@/components/profiles/HelpMenuBlade';
import { useRoles } from '@/contexts/RolesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getRegionNatIp, getRegionLabel } from '@/constants/regions';
import { useAgentManifest } from '@/hooks/useAgentManifest';
import { useError } from '@/contexts/ErrorContext';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import { SyncRequestSyncMode } from '@/api/control/missionImpactAPI.schemas';

/**
 * Profile detail page with edit functionality and source/target management
 */
export default function ProfileDetailPage() {
  const { orgId, profileId } = useParams<{ orgId: string; profileId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdminOrOperator } = useRoles();
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const { isOpen: isHelpMenuOpen, toggle: toggleHelpMenu } = useHelpMenu();
  const { latestVersion } = useAgentManifest();

  // Copy state for profile ID
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Schedule dialog state
  const [scheduleToDelete, setScheduleToDelete] = useState<ScheduleDto | null>(null);
  const [createScheduleDialogOpen, setCreateScheduleDialogOpen] = useState(false);

  // Reset sync confirmation dialog state
  const [isResetSyncDialogOpen, setIsResetSyncDialogOpen] = useState(false);

  // Sync burst dialog state
  const [isBurstDialogOpen, setIsBurstDialogOpen] = useState(false);

  if (!orgId) {
    logger.error(new Error('No org ID found in route params'), {
      context: 'ProfileDetailPage.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  if (!profileId) {
    logger.error(new Error('No profile ID found in route params'), {
      context: 'ProfileDetailPage.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  // Fetch profile data
  const {
    data: profileData,
    isLoading,
    error,
    refetch,
  } = useGetProfilesById(orgId, profileId, {
    query: {
      enabled: !!profileId,
    },
  });

  const profile = profileData?.data;

  // Burst state, used both for the at-a-glance badge and the burst-aware "Next Sync".
  const burstExpiresUtc = readBurstExpiresUtc(profile);
  const isBurstActive = burstExpiresUtc != null && new Date(burstExpiresUtc).getTime() > Date.now();

  // Fetch schedules data
  const {
    data: schedulesResponse,
    isLoading: isLoadingSchedules,
    error: schedulesError,
    refetch: refetchSchedules,
  } = useGetSchedules(orgId, profileId, {
    query: {
      enabled: !!orgId && !!profileId,
    },
  });

  const schedules: ScheduleDto[] = schedulesResponse?.data ?? [];

  // Fetch source tables to check if any exist
  const { data: sourceTablesData } = useQuery({
    queryKey: ['source-tables', profile?.source?.id],
    queryFn: async () => {
      if (!profile?.source?.id) return null;
      const response = await getSourceTables(orgId, profileId, profile.source.id, {
        pageSize: 1,
      });
      return response;
    },
    enabled: !!profile?.source?.id,
    retry: 1,
  });

  const hasTables = (sourceTablesData?.data.items?.length ?? 0) > 0;

  // Fetch agent data to check for outdated version
  const targetAgentId = profile?.target?.agentId;
  const { data: agentResponse } = useGetAgentsById(orgId, targetAgentId ?? '', {
    query: {
      enabled: !!targetAgentId,
    },
  });
  const assignedAgent = agentResponse?.data;

  // Toggle enable/disable mutation
  const { mutate: updateProfile, isPending: isToggling } = usePutProfiles({
    mutation: {
      onSuccess: (_, variables) => {
        // Invalidate and refetch the profile data
        void queryClient.invalidateQueries({
          queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
        });
        toast.success(
          variables.data.isEnabled
            ? t.profileDetail.errors.profileEnabled
            : t.profileDetail.errors.profileDisabled
        );
      },
      onError: (error) => {
        handleApiError(error, { context: 'ProfileDetailPage.updateProfile', profile: profileData?.data });
      },
    },
  });

  // Sync mutation
  const { mutate: syncProfile, isPending: isSyncing } = usePostProfileSync({
    mutation: {
      onSuccess: () => {
        toast.success(t.profileDetail.errors.syncTriggered);
      },
      onError: (error) => {
        handleApiError(error, { context: 'ProfileDetailPage.syncProfile', profile: profileData?.data });
      },
    },
  });

  /**
   * Handles toggling the enabled/disabled state
   */
  const handleToggleState = (checked: boolean) => {
    if (!profile) return;
    updateProfile({
      orgId: orgId,
      profileId: profileId,
      data: {
        name: profile.name,
        regionId: profile.regionId,
        isEnabled: checked,
      },
    });
  };

  /**
   * Handles triggering a sync with the specified mode
   */
  const handleSync = (syncMode: SyncRequestSyncMode) => {
    syncProfile({
      orgId: orgId,
      profileId: profileId,
      data: {
        syncMode,
      },
    });
  };

  /**
   * Handles confirming the reset and full sync
   */
  const handleConfirmResetSync = () => {
    handleSync(SyncRequestSyncMode.ResetAndFull);
  };

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedField(fieldName);
        toast.success(t.profileDetail.errors.copiedToClipboard);
        setTimeout(() => { setCopiedField(null); }, 2000);
      })
      .catch(() => {
        toast.error(t.profileDetail.errors.failedToCopy);
      });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-4 w-80" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>

        {/* Source card skeleton */}
        <div className="border rounded-lg p-6 space-y-4">
          <Skeleton className="h-6 w-32" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>

        {/* Target card skeleton */}
        <div className="border rounded-lg p-6 space-y-4">
          <Skeleton className="h-6 w-32" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-6">
        <QueryErrorState
          error={error ?? new Error('Profile not found')}
          message={t.errors.profiles.failedToLoadProfileDetails}
          onRetry={() => { void refetch(); }}
        />
      </div>
    );
  }

  // Determine if setup is incomplete and which step to resume at
  const hasSource = !!profile.source;
  const hasTarget = !!profile.target;
  const isSetupIncomplete = !hasSource || !hasTarget;
  // Static outbound (NAT gateway) IP for this profile's region — customers allow-list
  // this on their source/target firewalls. Shown at the profile level as it affects both.
  const outboundIp = getRegionNatIp(profile.regionId);
  const hasTriggeredInitialSync = profile.hasTriggeredInitialSync;
  const showSyncButtons = !isSetupIncomplete && hasTriggeredInitialSync;
  const showTriggerInitialSync = !isSetupIncomplete && !hasTriggeredInitialSync;
  // Hide enable switch when profile is disabled and hasn't triggered initial sync
  // Keep visible during toggle to prevent infinite loop
  const showEnableSwitch = !isSetupIncomplete || profile.isEnabled || hasTriggeredInitialSync || isToggling;

  const getResumeWizardUrl = () => {
    if (!hasSource) {
      return `/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`;
    }
    if (!hasTarget) {
      return `/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`;
    }
    // If has both source and target, go to tables step (in case tables weren't added)
    return `/dashboard/orgs/${orgId}/profiles/${profileId}/setup/tables`;
  };

  return (
    <div className="flex flex-row gap-0 md:gap-6 min-h-full">
      {/* Main Content Area */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Header with profile name, ID, and action buttons */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">{profile.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              {/* Status Badge */}
              <Badge
                variant={profile.isEnabled ? 'default' : 'destructive'}
                className={`text-base px-3 py-1 ${
                  profile.isEnabled
                    ? 'background-success-strong text-success-strong hover:opacity-90'
                    : 'background-error-strong text-error-strong hover:opacity-90'
                }`}
              >
                {profile.isEnabled ? t.profileDetail.status.active : t.profileDetail.status.paused}
              </Badge>
              {/* Burst indicator - visible at a glance while a burst is active */}
              {isBurstActive && (
                <Badge
                  variant="secondary"
                  className="text-base px-3 py-1 flex items-center gap-1"
                >
                  <Zap className="h-4 w-4" />
                  {t.profileDetail.burst.activeBadge}
                </Badge>
              )}
            </div>
            {outboundIp && (
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <span className="font-medium">{t.profileDetail.outboundIp}:</span>
                <code className="rounded bg-muted px-2 py-0.5 font-mono text-xs select-all">
                  {outboundIp}
                </code>
                <span>({getRegionLabel(profile.regionId)})</span>
              </div>
            )}
            <div className="group flex items-center gap-2 mt-1">
              <button
                onClick={() => { handleCopy(profile.id, 'profileId'); }}
                className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:background-neutral rounded"
                title={t.profileDetail.actions.copyProfileId}
              >
                {copiedField === 'profileId' ? (
                  <Check className="h-3 w-3 text-success" />
                ) : (
                  <Copy className="h-3 w-3 text-neutral" />
                )}
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Help Button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9"
              onClick={toggleHelpMenu}
              title={t.profileDetail.actions.help}
            >
              <HelpCircle className="h-4 w-4" />
            </Button>

            {/* Mobile Hamburger Menu (hidden on lg+) */}
            <div className="lg:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9">
                    <Menu className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {/* Toggle Enable/Disable */}
                  {isAdminOrOperator && showEnableSwitch && (
                    <>
                      <DropdownMenuItem
                        onClick={() => { if (!isToggling) handleToggleState(!profile.isEnabled); }}
                        disabled={isToggling}
                      >
                        <Power className="h-4 w-4 mr-2" />
                        {isToggling
                          ? t.profileDetail.actions.updating
                          : profile.isEnabled
                            ? t.profileDetail.actions.disable
                            : t.profileDetail.actions.enable}
                      </DropdownMenuItem>
                      {/* Sync - only show if setup is complete */}
                      {showSyncButtons && (
                        <>
                          <DropdownMenuItem
                            onClick={() => { handleSync(SyncRequestSyncMode.Incremental); }}
                            disabled={isSyncing || !profile.isEnabled}
                            title={
                              !profile.isEnabled
                                ? t.profileDetail.actions.syncDisabledTooltip
                                : undefined
                            }
                          >
                            <RefreshCw
                              className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                            />
                            {isSyncing
                              ? t.profileDetail.actions.syncing
                              : t.profileDetail.actions.sync}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setIsResetSyncDialogOpen(true); }}
                            disabled={isSyncing || !profile.isEnabled}
                            title={
                              !profile.isEnabled
                                ? t.profileDetail.actions.syncDisabledTooltip
                                : undefined
                            }
                          >
                            <RefreshCw
                              className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                            />
                            {t.profileDetail.actions.resetFullSync}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setIsBurstDialogOpen(true); }}
                            disabled={!profile.isEnabled}
                            title={
                              !profile.isEnabled
                                ? t.profileDetail.actions.syncDisabledTooltip
                                : undefined
                            }
                          >
                            <Zap className="h-4 w-4 mr-2" />
                            {t.profileDetail.burst.menuItem}
                          </DropdownMenuItem>
                        </>
                      )}
                      {/* Trigger Initial Sync - only show if setup is complete but initial sync hasn't been triggered */}
                      {showTriggerInitialSync && (
                        <DropdownMenuItem
                          onClick={() => { handleSync(SyncRequestSyncMode.ResetAndFull); }}
                          disabled={isSyncing || !profile.isEnabled}
                          title={
                            !profile.isEnabled
                              ? t.profileDetail.actions.syncDisabledTooltip
                              : undefined
                          }
                        >
                          <RefreshCw
                            className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                          />
                          {isSyncing
                            ? t.profileDetail.actions.syncing
                            : t.profileDetail.actions.triggerInitialSync}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                    </>
                  )}

                  {/* Resume Setup - only show if incomplete */}
                  {isSetupIncomplete && isAdminOrOperator && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { void navigate(getResumeWizardUrl()); }}>
                        <Wand2 className="h-4 w-4 mr-2" />
                        {t.profileDetail.actions.resumeSetup}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}

                  {/* Edit Profile */}
                  <EditProfileDialog
                    orgId={orgId}
                    profile={profile}
                    onSuccess={() => { void refetch(); }}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); }}>
                        <Pencil className="h-4 w-4 mr-2" />
                        {t.profileDetail.actions.editProfile}
                      </DropdownMenuItem>
                    }
                  />

                  {/* Delete Profile */}
                  <DropdownMenuSeparator />
                  <DeleteProfileDialog
                    orgId={orgId}
                    profile={profile}
                    trigger={
                      <DropdownMenuItem variant="destructive" onSelect={(e) => { e.preventDefault(); }}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t.profileDetail.actions.deleteProfile}
                      </DropdownMenuItem>
                    }
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Desktop Buttons (hidden on mobile/tablet, shown on lg+) */}
            <div className="hidden lg:flex lg:gap-2">
              {/* Toggle Enable/Disable Switch */}
              {isAdminOrOperator && showEnableSwitch && (
                <div
                  onClick={() => { if (!isToggling) handleToggleState(!profile.isEnabled); }}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border border-input bg-background shadow-sm h-9 px-4 py-2 ${
                    isToggling
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-accent hover:text-accent-foreground cursor-pointer'
                  }`}
                >
                  <Switch
                    id="profileEnabled"
                    checked={profile.isEnabled}
                    disabled={isToggling}
                    className="pointer-events-none"
                  />
                  <span className="ml-2">{t.profileDetail.actions.enabled}</span>
                </div>
              )}

              {/* Sync Button - only show if setup is complete and initial sync has been triggered */}
              {isAdminOrOperator && showSyncButtons && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <RefreshButton
                        variant="outline"
                        className="h-9"
                        onClick={() => { handleSync(SyncRequestSyncMode.Incremental); }}
                        isLoading={isSyncing}
                        disabled={!profile.isEnabled}
                      >
                        {t.profileDetail.actions.sync}
                      </RefreshButton>
                    </span>
                  </TooltipTrigger>
                  {!profile.isEnabled && (
                    <TooltipContent>{t.profileDetail.actions.syncDisabledTooltip}</TooltipContent>
                  )}
                </Tooltip>
              )}

              {/* Trigger Initial Sync Button - only show if setup is complete but initial sync hasn't been triggered */}
              {isAdminOrOperator && showTriggerInitialSync && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <RefreshButton
                        variant="outline"
                        className="h-9"
                        onClick={() => { handleSync(SyncRequestSyncMode.ResetAndFull); }}
                        isLoading={isSyncing}
                        disabled={!profile.isEnabled}
                      >
                        {t.profileDetail.actions.triggerInitialSync}
                      </RefreshButton>
                    </span>
                  </TooltipTrigger>
                  {!profile.isEnabled && (
                    <TooltipContent>{t.profileDetail.actions.syncDisabledTooltip}</TooltipContent>
                  )}
                </Tooltip>
              )}

              {/* Resume Setup Button - only show if setup is incomplete */}
              {isSetupIncomplete && isAdminOrOperator && (
                <Link to={getResumeWizardUrl()}>
                  <Button variant="outline" className="h-9">
                    <Wand2 className="h-4 w-4 mr-2" />
                    {t.profileDetail.actions.resumeSetup}
                  </Button>
                </Link>
              )}

              {/* Profile Actions Dropdown Menu */}
              {isAdminOrOperator && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="h-9">
                      <EllipsisVertical className="h-4 w-4 mr-2" />
                      {t.profileDetail.actions.actions}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* Edit Profile */}
                    <EditProfileDialog
                      orgId={orgId}
                      profile={profile}
                      onSuccess={() => { void refetch(); }}
                      trigger={
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          {t.profileDetail.actions.editProfile}
                        </DropdownMenuItem>
                      }
                    />
                    {/* Reset + Full Sync - only show if initial sync has been triggered */}
                    {showSyncButtons && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => { setIsResetSyncDialogOpen(true); }}
                          disabled={isSyncing || !profile.isEnabled}
                          title={
                            !profile.isEnabled
                              ? t.profileDetail.actions.syncDisabledTooltip
                              : undefined
                          }
                        >
                          <RefreshCw
                            className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                          />
                          {t.profileDetail.actions.resetFullSync}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setIsBurstDialogOpen(true); }}
                          disabled={!profile.isEnabled}
                          title={
                            !profile.isEnabled
                              ? t.profileDetail.actions.syncDisabledTooltip
                              : undefined
                          }
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          {t.profileDetail.burst.menuItem}
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* Delete Profile */}
                    <DropdownMenuSeparator />
                    <DeleteProfileDialog
                      orgId={orgId}
                      profile={profile}
                      trigger={
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={(e) => { e.preventDefault(); }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t.profileDetail.actions.deleteProfile}
                        </DropdownMenuItem>
                      }
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* Agent offline banner - the profile is paused until its agent is back online */}
        <AgentOfflineProfileAlert agent={assignedAgent} />

        {/* Source Card */}
        <SourceCard
          orgId={orgId}
          profileId={profileId}
          source={profile.source}
          onTestSuccess={() => { void refetch(); }}
          resumeSetupUrl={isSetupIncomplete ? getResumeWizardUrl() : undefined}
        />

        {/* Target Card */}
        <TargetCard
          orgId={orgId}
          profileId={profileId}
          target={profile.target}
          onTestSuccess={() => { void refetch(); }}
          resumeSetupUrl={isSetupIncomplete ? getResumeWizardUrl() : undefined}
          isProfileEnabled={profile.isEnabled}
          agent={assignedAgent}
          latestVersion={latestVersion}
          regionId={profile.regionId}
          source={profile.source}
          hasTriggeredInitialSync={profile.hasTriggeredInitialSync}
          burstExpiresUtc={burstExpiresUtc}
        />

        {/* Schedules Card */}
        <QuietTimeCard
          isLoading={isLoadingSchedules}
          error={schedulesError}
          schedules={schedules}
          canEdit={isAdminOrOperator}
          onRefetch={() => { void refetchSchedules(); }}
          onCreateSchedule={() => { setCreateScheduleDialogOpen(true); }}
          onDeleteSchedule={setScheduleToDelete}
        />

        {/* Run History Section */}
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold">{t.profileDetail.runHistory.title}</h2>
            <p className="text-muted-foreground">{t.profileDetail.runHistory.description}</p>
          </div>

          <RunMetadataTable
            orgId={orgId}
            profileIds={profileId ? [profileId] : undefined}
            showProfileColumn={false}
            title={t.profileDetail.runHistory.recentRuns}
            storageKey="profileDetail.columnVisibility"
          />
        </div>

        {/* Schedule Dialogs */}
        <CreateScheduleDialog
          orgId={orgId}
          profileId={profileId}
          open={createScheduleDialogOpen}
          onOpenChange={setCreateScheduleDialogOpen}
        />
        <DeleteScheduleDialog
          orgId={orgId}
          profileId={profileId}
          schedule={scheduleToDelete}
          open={!!scheduleToDelete}
          onOpenChange={(open) => { if (!open) setScheduleToDelete(null); }}
        />

        {/* Reset Sync Confirmation Dialog */}
        <SyncConfirmationDialog
          open={isResetSyncDialogOpen}
          onOpenChange={setIsResetSyncDialogOpen}
          onConfirm={handleConfirmResetSync}
          isSyncing={isSyncing}
        />

        {/* Sync Burst Dialog */}
        <BurstDialog
          orgId={orgId}
          profileId={profileId}
          open={isBurstDialogOpen}
          onOpenChange={setIsBurstDialogOpen}
          burstExpiresUtc={readBurstExpiresUtc(profile)}
          schedules={schedules}
        />
      </div>

      {/* Help Menu Blade */}
      <HelpMenuBlade
        isOpen={isHelpMenuOpen}
        onClose={toggleHelpMenu}
        title={t.profileDetail.helpContent.setupChecklistTitle}
        description={t.profileDetail.helpContent.setupChecklistDescription}
        hasSource={!!profile.source}
        hasTarget={!!profile.target}
        hasTables={hasTables}
        hasQuietTime={schedules.length > 0}
        hasDeleteMode={!!profile.deleteMode}
        hasTriggeredInitialSync={profile.hasTriggeredInitialSync}
        orgId={orgId}
        profileId={profileId}
        showResumeSetup={isSetupIncomplete && isAdminOrOperator}
      />
    </div>
  );
}
